import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import EventsSelectorMenu from "./EventsSelectorMenu";
import Icon from "../../../../shared/components/Icon";
import EventPropertyFilterModule from "../../../conversion-events/components/EventPropertyFilterModule";
import { EventPropertyFilterChip } from "../../../conversion-events/components/EventsFilterModule";

/**
 * FlowQueryConfig — query builder for the Feature Flow feature.
 *
 * Matches the FunnelQueryConfig pattern:
 *  - Numbered event rows (unlimited events)
 *  - Per-event filter button + chips
 *  - stepsBefore / stepsAfter spinners per event
 *  - Conversion window + criteria (shown when ≥ 2 events)
 */
export default function FlowQueryConfig({
  anchorEvents = [],
  onAnchorEventsChange,
  conversionWindow = { value: 7, unit: "days" },
  onConversionWindowChange,
  conversionCriteria = "distinct_id",
  onConversionCriteriaChange,
  availableEvents = [],
  teamId = null,
}) {
  const [stepMenuOpen, setStepMenuOpen] = useState(null); // null | number (index) | 'add'
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const menuRef = useRef(null);
  const addButtonRef = useRef(null);

  // Per-event filter state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedEventForFilter, setSelectedEventForFilter] = useState(null);
  const [selectedEventIndex, setSelectedEventIndex] = useState(null);
  const [editingFilter, setEditingFilter] = useState(null);
  const filterButtonRefs = useRef({});
  const filterChipRefs = useRef({});

  useEffect(() => {
    const handler = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        addButtonRef.current &&
        !addButtonRef.current.contains(e.target)
      ) {
        setStepMenuOpen(null);
        setMenuAnchorRect(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [stepMenuOpen]);

  // ── Event management ──────────────────────────────────────────────────────

  const handleAddEvent = (eventName) => {
    const id = `anchor_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    onAnchorEventsChange([
      ...anchorEvents,
      { id, eventName: eventName || "", label: eventName || "", stepsBefore: 3, stepsAfter: 3, filters: [] },
    ]);
    setStepMenuOpen(null);
    setMenuAnchorRect(null);
  };

  const handleReplaceEvent = (index, eventName) => {
    const next = anchorEvents.map((a, i) =>
      i === index ? { ...a, eventName: eventName || "", label: eventName || "" } : a
    );
    onAnchorEventsChange(next);
    setStepMenuOpen(null);
    setMenuAnchorRect(null);
  };

  const handleRemoveEvent = (index) => {
    onAnchorEventsChange(anchorEvents.filter((_, i) => i !== index));
  };

  const handleStepChange = (index, field, value) => {
    const next = anchorEvents.map((a, i) =>
      i === index ? { ...a, [field]: Math.min(10, Math.max(1, parseInt(value, 10) || 1)) } : a
    );
    onAnchorEventsChange(next);
  };

  const handleUpdateEvent = (index, updates) => {
    const next = anchorEvents.map((a, i) => (i === index ? { ...a, ...updates } : a));
    onAnchorEventsChange(next);
  };

  // ── Per-event filter management ───────────────────────────────────────────

  const handleFilterClick = (event, eventIndex) => {
    if (!event.eventName) return;
    setSelectedEventForFilter({ id: event.eventName, eventName: event.eventName, eventType: "custom" });
    setSelectedEventIndex(eventIndex);
    setIsFilterOpen(true);
  };

  const handleFilterAdd = (filter) => {
    if (selectedEventIndex === null || !selectedEventForFilter) return;
    const currentEvent = anchorEvents[selectedEventIndex];
    const currentFilters = currentEvent.filters || [];
    const normalized = {
      id: filter.id || `ef-${Date.now()}-${Math.random()}`,
      field: filter.property || filter.field,
      property: filter.property || filter.field,
      operator: filter.operator,
      value: filter.value,
      value2: filter.value2,
      propertyType: filter.propertyType,
      propertySource: filter.propertySource,
      propertyLabel: filter.propertyLabel || filter.property || filter.field,
      category: "event_properties",
      eventName: selectedEventForFilter.eventName,
      listMode: filter.listMode,
      listItemType: filter.listItemType,
      sampleValues: filter.sampleValues || [],
      connector: currentFilters.length === 0 ? "WHERE" : "AND",
    };
    if (editingFilter) {
      handleUpdateEvent(selectedEventIndex, {
        filters: currentFilters.map((f) => (f.id === editingFilter.id ? normalized : f)),
      });
      setEditingFilter(null);
    } else {
      handleUpdateEvent(selectedEventIndex, { filters: [...currentFilters, normalized] });
    }
    setIsFilterOpen(false);
    setSelectedEventForFilter(null);
    setSelectedEventIndex(null);
  };

  const handleFirstTimeChange = (enabled) => {
    if (selectedEventIndex === null) return;
    const currentFilters = anchorEvents[selectedEventIndex]?.filters || [];
    if (enabled) {
      const without = currentFilters.filter((f) => f.category !== "first_time");
      handleUpdateEvent(selectedEventIndex, {
        filters: [
          ...without,
          {
            id: "first-time-filter",
            field: "firstTime",
            property: "firstTime",
            operator: "equals",
            value: true,
            category: "first_time",
            eventName: selectedEventForFilter?.eventName,
            connector: without.length === 0 ? "WHERE" : "AND",
          },
        ],
      });
    } else {
      handleUpdateEvent(selectedEventIndex, {
        filters: currentFilters.filter((f) => f.category !== "first_time"),
      });
    }
    setIsFilterOpen(false);
    setSelectedEventForFilter(null);
    setSelectedEventIndex(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const eventsList = Array.isArray(availableEvents) ? availableEvents : [];
  const hasMultiple = anchorEvents.length >= 2;

  const CRITERIA_OPTIONS = [
    { value: "distinct_id", label: "By Distinct ID" },
    { value: "totals", label: "Totals" },
    { value: "sessions", label: "By Sessions" },
  ];

  const WINDOW_UNITS = ["seconds", "minutes", "hours", "days", "weeks", "months"];

  return (
    <>
      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textMuted }}>
          Events
        </span>
      </div>

      {/* Event rows */}
      {anchorEvents.length === 0 ? (
        <div
          className="px-3 py-2.5 border border-dashed rounded-lg text-sm text-center mb-4"
          style={{ borderColor: theme.border, color: theme.textMuted }}
        >
          Add an event below to start.
        </div>
      ) : (
        <div className="mb-4 space-y-2">
          {anchorEvents.map((anchor, index) => {
            const eventFilters = (anchor.filters || []).filter((f) => f.category !== "first_time");
            const hasFilters = eventFilters.length > 0;
            const letter = String.fromCharCode(65 + index); // A, B, C…

            return (
              <div
                key={anchor.id || index}
                className="border rounded-lg"
                style={{ borderColor: theme.border }}
              >
                {/* Event row */}
                <div
                  className="relative flex items-center gap-2 px-3 py-2.5 text-sm"
                  style={{ borderBottom: `1px solid ${theme.border}` }}
                >
                  <span className="text-xs flex-shrink-0" style={{ color: theme.textMuted }}>
                    {letter}.
                  </span>
                  <span
                    className="truncate flex-1 min-w-0 cursor-pointer"
                    style={{ color: anchor.eventName ? theme.textPrimary : theme.textMuted }}
                    onClick={(e) => {
                      if (stepMenuOpen === index) {
                        setStepMenuOpen(null);
                        setMenuAnchorRect(null);
                      } else {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setMenuAnchorRect({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 220) });
                        setStepMenuOpen(index);
                      }
                    }}
                    title={anchor.eventName ? "Change event" : "Select event"}
                  >
                    {anchor.eventName || "Select event…"}
                  </span>
                  {anchor.eventName && (
                    <button
                      ref={(el) => { filterButtonRefs.current[`ev-${index}`] = el; }}
                      type="button"
                      onClick={() => handleFilterClick(anchor, index)}
                      className="p-1.5 hover:bg-gray-100 rounded flex-shrink-0"
                      title="Add filter"
                    >
                      <Icon name="filter" size={16} color={hasFilters ? "#4B57AA" : "#6b7280"} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveEvent(index)}
                    className="p-1 rounded hover:bg-gray-100 flex-shrink-0"
                    title="Remove event"
                  >
                    <Icon name="delete" size={15} color="#DC2626" />
                  </button>
                </div>

                {/* Steps before / after — inside the card */}
                <div
                  className="flex items-center gap-3 px-3 pb-2 pt-0"
                  style={{ borderTop: "none" }}
                >
                  <span className="text-xs flex-shrink-0" style={{ color: theme.textMuted }}>
                    Before
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={anchor.stepsBefore ?? 3}
                    onChange={(e) => handleStepChange(index, "stepsBefore", e.target.value)}
                    className="w-12 px-1.5 py-0.5 rounded border text-xs text-center"
                    style={{ borderColor: theme.border, background: theme.surface, color: theme.textPrimary }}
                  />
                  <span className="text-xs flex-shrink-0" style={{ color: theme.textMuted }}>
                    After
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={anchor.stepsAfter ?? 3}
                    onChange={(e) => handleStepChange(index, "stepsAfter", e.target.value)}
                    className="w-12 px-1.5 py-0.5 rounded border text-xs text-center"
                    style={{ borderColor: theme.border, background: theme.surface, color: theme.textPrimary }}
                  />
                </div>

                {/* Filter chips */}
                {hasFilters && (
                  <div className="px-3 pb-2 space-y-1">
                    {eventFilters.map((filter, fi) => {
                      if (!filterChipRefs.current[filter.id]) {
                        filterChipRefs.current[filter.id] = React.createRef();
                      }
                      return (
                        <EventPropertyFilterChip
                          key={filter.id}
                          filter={filter}
                          isFirst={fi === 0}
                          filterRef={filterChipRefs.current[filter.id]}
                          onEdit={() => {
                            setEditingFilter(filter);
                            setSelectedEventForFilter({ id: anchor.eventName, eventName: anchor.eventName, eventType: "custom" });
                            setSelectedEventIndex(index);
                            setIsFilterOpen(true);
                          }}
                          onUpdate={(updates) => {
                            handleUpdateEvent(index, {
                              filters: (anchor.filters || []).map((f) =>
                                f.id === filter.id ? { ...f, ...updates } : f
                              ),
                            });
                          }}
                          onRemove={() => {
                            const updated = (anchor.filters || [])
                              .filter((f) => f.id !== filter.id)
                              .map((f, idx) => ({ ...f, connector: idx === 0 ? "WHERE" : f.connector || "AND" }));
                            handleUpdateEvent(index, { filters: updated });
                          }}
                          teamId={teamId}
                          eventName={anchor.eventName}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Portal-rendered event selector dropdown for per-card event selection */}
      {typeof stepMenuOpen === "number" && menuAnchorRect &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: menuAnchorRect.top,
              left: menuAnchorRect.left,
              width: menuAnchorRect.width,
              zIndex: 9999,
            }}
          >
            <EventsSelectorMenu
              availableEvents={eventsList}
              onSelect={(name) => handleReplaceEvent(stepMenuOpen, name)}
              onClose={() => { setStepMenuOpen(null); setMenuAnchorRect(null); }}
              maxHeight={192}
            />
          </div>,
          document.body
        )}

      {/* Add event button */}
      <div className="relative mb-6">
        <button
          ref={addButtonRef}
          type="button"
          onClick={() => setStepMenuOpen(stepMenuOpen === "add" ? null : "add")}
          className="w-full flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg text-sm"
          style={{ borderColor: theme.border, color: theme.textMuted }}
        >
          <span className="text-gray-500">+</span>
          <span className="flex-1 text-left">Add event</span>
          <ChevronDown className="w-4 h-4" />
        </button>
        {stepMenuOpen === "add" && (
          <div ref={menuRef} className="absolute left-0 right-0 top-full mt-1 z-[100]">
            <EventsSelectorMenu
              availableEvents={eventsList}
              onSelect={(name) => handleAddEvent(name)}
              onClose={() => setStepMenuOpen(null)}
              maxHeight={192}
            />
          </div>
        )}
      </div>

      {/* Conversion window + criteria (shown when ≥ 2 events) */}
      {hasMultiple && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textMuted }}>
              Window
            </span>
          </div>
          <div className="flex gap-2">
            <input
              type="number"
              min={1}
              value={conversionWindow.value}
              onChange={(e) =>
                onConversionWindowChange({ ...conversionWindow, value: parseInt(e.target.value, 10) || 1 })
              }
              className="flex-1 px-2 py-1.5 rounded-md border text-sm"
              style={{ borderColor: theme.border, background: theme.surface, color: theme.textPrimary }}
            />
            <select
              value={conversionWindow.unit}
              onChange={(e) => onConversionWindowChange({ ...conversionWindow, unit: e.target.value })}
              className="px-2 py-1.5 rounded-md border text-sm"
              style={{ borderColor: theme.border, background: theme.surface, color: theme.textPrimary }}
            >
              {WINDOW_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-wider flex-shrink-0" style={{ color: theme.textMuted }}>
              Count by
            </span>
            <select
              value={conversionCriteria}
              onChange={(e) => onConversionCriteriaChange(e.target.value)}
              className="flex-1 px-2 py-1.5 rounded-md border text-sm"
              style={{ borderColor: theme.border, background: theme.surface, color: theme.textPrimary }}
            >
              {CRITERIA_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Per-event filter picker */}
      {isFilterOpen && selectedEventForFilter && (
        <EventPropertyFilterModule
          isOpen={isFilterOpen}
          onClose={() => {
            setIsFilterOpen(false);
            setSelectedEventForFilter(null);
            setSelectedEventIndex(null);
            setEditingFilter(null);
          }}
          triggerRef={
            editingFilter && filterChipRefs.current[editingFilter.id]
              ? filterChipRefs.current[editingFilter.id]
              : filterButtonRefs.current[`ev-${selectedEventIndex}`]
          }
          eventName={selectedEventForFilter.eventName}
          eventId={selectedEventForFilter.id}
          teamId={teamId}
          filters={selectedEventIndex !== null ? (anchorEvents[selectedEventIndex]?.filters || []) : []}
          onFiltersChange={handleFilterAdd}
          existingFilter={editingFilter}
          firstTimeOnly={
            selectedEventIndex !== null
              ? (anchorEvents[selectedEventIndex]?.filters || []).some((f) => f.category === "first_time")
              : false
          }
          onFirstTimeChange={handleFirstTimeChange}
        />
      )}
    </>
  );
}
